// Holiday mode (temporary schedule) start/end values are stored as a naive 'YYYY-MM-DD HH:MM:SS'
// wall-clock string in the timezone the schedule was created in (see the "timezone" field on the item) -
// they are NOT UTC. This converts that wall-clock string from one IANA timezone to another, returning
// a naive string in the same format, representing the equivalent wall-clock time in the target zone.
const getOffsetMinutes = (timeZone, date) => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).formatToParts(date).reduce((acc, part) => {
        acc[part.type] = part.value;
        return acc;
    }, {});

    const asUTC = Date.UTC(
        parts.year, parts.month - 1, parts.day,
        parts.hour === '24' ? 0 : parts.hour, parts.minute, parts.second
    );

    return (asUTC - date.getTime()) / 60000;
};

const pad = (value) => String(value).padStart(2, '0');

export const UseConvertZonedDateTime = (dateTimeString, fromTimeZone, toTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
    if (!dateTimeString) {
        return dateTimeString;
    }

    if (!fromTimeZone || fromTimeZone === toTimeZone) {
        return dateTimeString;
    }

    const normalized = String(dateTimeString).trim().replace('T', ' ');
    const [datePart, timePart = '00:00:00'] = normalized.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second = 0] = timePart.split(':').map(Number);

    if ([year, month, day, hour, minute].some(Number.isNaN)) {
        return dateTimeString;
    }

    // resolve the actual UTC instant this wall-clock time corresponds to in fromTimeZone
    const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second || 0);
    const offsetMinutes = getOffsetMinutes(fromTimeZone, new Date(utcGuess));
    const actualInstant = new Date(utcGuess - offsetMinutes * 60000);

    // read that instant's wall-clock components back out in toTimeZone
    const targetParts = new Intl.DateTimeFormat('en-US', {
        timeZone: toTimeZone,
        hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).formatToParts(actualInstant).reduce((acc, part) => {
        acc[part.type] = part.value;
        return acc;
    }, {});

    const targetHour = targetParts.hour === '24' ? '00' : targetParts.hour;

    return `${targetParts.year}-${pad(targetParts.month)}-${pad(targetParts.day)} ${pad(targetHour)}:${pad(targetParts.minute)}:${pad(targetParts.second)}`;
}

// Parses a naive 'YYYY-MM-DD HH:MM:SS' string into a Date using the multi-arg Date constructor, which is
// always treated as local time consistently across browsers. Never feed this kind of string to `new
// Date(string)` directly - space-separated, non-ISO datetime strings are implementation-defined per spec
// (e.g. Chrome parses them as local time, Safari parses them as UTC), which silently shifts the result by
// the viewer's UTC offset depending on browser.
export const UseParseNaiveDateTime = (dateTimeString) => {
    if (!dateTimeString) {
        return new Date(NaN);
    }

    const normalized = String(dateTimeString).trim().replace('T', ' ');
    const [datePart, timePart = '00:00:00'] = normalized.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour = 0, minute = 0, second = 0] = timePart.split(':').map(Number);

    return new Date(year, month - 1, day, hour, minute, second || 0);
}
